/**
 * RTK Query endpoints for the agent-factory service (@cluster/agent-factory).
 * Endpoints are injected into the shared `apiSlice` so they share the same
 * baseQuery (token refresh) and cache tag space. Base path after the dev
 * proxy + nginx rewrite strips `/api`: `/agent/providers`, `/agent/models`,
 * `/agent/model-collections`, `/agent/factories`, `/agent/triggers`.
 */
import { apiSlice } from './apiSlice';
import type {
  AgentProviderConfig,
  CreateProviderDto,
  UpdateProviderDto,
  ProviderTestResult,
  AgentModelConfig,
  CreateModelDto,
  UpdateModelDto,
  ModelTestResult,
  AgentModelCollection,
  CreateModelCollectionDto,
  UpdateModelCollectionDto,
  AgentFactory,
  CreateFactoryDto,
  UpdateFactoryDto,
  TriggerBinding,
  CreateTriggerDto,
  UpdateTriggerDto,
} from '@/types';

function data<T>(response: { data: T }): T {
  return response.data;
}

/**
 * agent-factory serializes its view objects with `id` (e.g. `{ id, name, ... }`),
 * while the UI types (AgentProviderConfig, AgentModelConfig, AgentModelCollection,
 * AgentFactory, TriggerBinding) are declared with `_id`. Alias the backend `id`
 * into `_id` so Radix Select values, table rows and navigations keep working.
 * The helpers accept `unknown` because RTK Query types the transformResponse
 * raw result as `unknown`; we narrow the `{ data }` envelope at runtime.
 */
function isEnvelope(x: unknown): x is { data: unknown } {
  return typeof x === 'object' && x !== null && 'data' in x;
}
function aliasId<T extends object>(entry: T): T & { _id: string } {
  const e = entry as T & { id?: string; _id?: string };
  return { ...entry, _id: e.id ?? e._id ?? '' };
}
function dataList<T extends object>(response: unknown): T[] {
  const data = isEnvelope(response) ? response.data : undefined;
  return Array.isArray(data) ? data.map((x) => aliasId<T>(x as T)) : [];
}
function dataOne<T extends object>(response: unknown): T & { _id: string } {
  const data = isEnvelope(response) ? response.data : undefined;
  return aliasId<T>((data as T) ?? ({} as T));
}

export interface TriggerListParams {
  factoryId?: string;
}

export const agentFactoryApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Providers ─────────────────────────────────────────────────────────
    getAgentProviders: builder.query<AgentProviderConfig[], void>({
      query: () => '/agent/providers',
      transformResponse: (r) => dataList<AgentProviderConfig>(r),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'AgentProvider' as const, id: _id })),
              { type: 'AgentProvider', id: 'LIST' },
            ]
          : [{ type: 'AgentProvider', id: 'LIST' }],
    }),

    getAgentProvider: builder.query<AgentProviderConfig, string>({
      query: (id) => `/agent/providers/${id}`,
      transformResponse: (r) => dataOne<AgentProviderConfig>(r),
      providesTags: (_result, _error, id) => [{ type: 'AgentProvider', id }],
    }),

    createAgentProvider: builder.mutation<AgentProviderConfig, CreateProviderDto>({
      query: (body) => ({ url: '/agent/providers', method: 'POST', body }),
      transformResponse: (r) => dataOne<AgentProviderConfig>(r),
      invalidatesTags: [{ type: 'AgentProvider', id: 'LIST' }],
    }),

    updateAgentProvider: builder.mutation<
      AgentProviderConfig,
      { id: string; body: UpdateProviderDto }
    >({
      query: ({ id, body }) => ({ url: `/agent/providers/${id}`, method: 'PATCH', body }),
      transformResponse: (r) => dataOne<AgentProviderConfig>(r),
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'AgentProvider', id }, { type: 'AgentProvider', id: 'LIST' }]
          : [{ type: 'AgentProvider', id: 'LIST' }],
    }),

    deleteAgentProvider: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/agent/providers/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'AgentProvider', id: 'LIST' }],
    }),

    testAgentProvider: builder.mutation<ProviderTestResult, string>({
      query: (id) => ({ url: `/agent/providers/${id}/test`, method: 'POST' }),
    }),

    // ── Models ────────────────────────────────────────────────────────────
    getAgentModels: builder.query<AgentModelConfig[], void>({
      query: () => '/agent/models',
      transformResponse: (r) => dataList<AgentModelConfig>(r),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'AgentModel' as const, id: _id })),
              { type: 'AgentModel', id: 'LIST' },
            ]
          : [{ type: 'AgentModel', id: 'LIST' }],
    }),

    getAgentModel: builder.query<AgentModelConfig, string>({
      query: (id) => `/agent/models/${id}`,
      transformResponse: (r) => dataOne<AgentModelConfig>(r),
      providesTags: (_result, _error, id) => [{ type: 'AgentModel', id }],
    }),

    createAgentModel: builder.mutation<AgentModelConfig, CreateModelDto>({
      query: (body) => ({ url: '/agent/models', method: 'POST', body }),
      transformResponse: (r) => dataOne<AgentModelConfig>(r),
      invalidatesTags: [{ type: 'AgentModel', id: 'LIST' }],
    }),

    updateAgentModel: builder.mutation<AgentModelConfig, { id: string; body: UpdateModelDto }>({
      query: ({ id, body }) => ({ url: `/agent/models/${id}`, method: 'PATCH', body }),
      transformResponse: (r) => dataOne<AgentModelConfig>(r),
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'AgentModel', id }, { type: 'AgentModel', id: 'LIST' }]
          : [{ type: 'AgentModel', id: 'LIST' }],
    }),

    deleteAgentModel: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/agent/models/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'AgentModel', id: 'LIST' }],
    }),

    testAgentModel: builder.mutation<ModelTestResult, string>({
      query: (id) => ({ url: `/agent/models/${id}/test`, method: 'POST' }),
    }),

    // ── Model collections ─────────────────────────────────────────────────
    getAgentModelCollections: builder.query<AgentModelCollection[], void>({
      query: () => '/agent/model-collections',
      transformResponse: (r) => dataList<AgentModelCollection>(r),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'AgentModelCollection' as const, id: _id })),
              { type: 'AgentModelCollection', id: 'LIST' },
            ]
          : [{ type: 'AgentModelCollection', id: 'LIST' }],
    }),

    getAgentModelCollection: builder.query<AgentModelCollection, string>({
      query: (id) => `/agent/model-collections/${id}`,
      transformResponse: (r) => dataOne<AgentModelCollection>(r),
      providesTags: (_result, _error, id) => [{ type: 'AgentModelCollection', id }],
    }),

    createAgentModelCollection: builder.mutation<
      AgentModelCollection,
      CreateModelCollectionDto
    >({
      query: (body) => ({ url: '/agent/model-collections', method: 'POST', body }),
      transformResponse: (r) => dataOne<AgentModelCollection>(r),
      invalidatesTags: [{ type: 'AgentModelCollection', id: 'LIST' }],
    }),

    updateAgentModelCollection: builder.mutation<
      AgentModelCollection,
      { id: string; body: UpdateModelCollectionDto }
    >({
      query: ({ id, body }) => ({ url: `/agent/model-collections/${id}`, method: 'PATCH', body }),
      transformResponse: (r) => dataOne<AgentModelCollection>(r),
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [
              { type: 'AgentModelCollection', id },
              { type: 'AgentModelCollection', id: 'LIST' },
            ]
          : [{ type: 'AgentModelCollection', id: 'LIST' }],
    }),

    deleteAgentModelCollection: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/agent/model-collections/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'AgentModelCollection', id: 'LIST' }],
    }),

    // ── Factories ─────────────────────────────────────────────────────────
    getAgentFactories: builder.query<AgentFactory[], void>({
      query: () => '/agent/factories',
      transformResponse: (r) => dataList<AgentFactory>(r),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'AgentFactory' as const, id: _id })),
              { type: 'AgentFactory', id: 'LIST' },
            ]
          : [{ type: 'AgentFactory', id: 'LIST' }],
    }),

    getAgentFactory: builder.query<AgentFactory, string>({
      query: (id) => `/agent/factories/${id}`,
      transformResponse: (r) => dataOne<AgentFactory>(r),
      providesTags: (_result, _error, id) => [{ type: 'AgentFactory', id }],
    }),

    createAgentFactory: builder.mutation<AgentFactory, CreateFactoryDto>({
      query: (body) => ({ url: '/agent/factories', method: 'POST', body }),
      transformResponse: (r) => dataOne<AgentFactory>(r),
      invalidatesTags: [{ type: 'AgentFactory', id: 'LIST' }],
    }),

    updateAgentFactory: builder.mutation<
      AgentFactory,
      { id: string; body: UpdateFactoryDto }
    >({
      query: ({ id, body }) => ({ url: `/agent/factories/${id}`, method: 'PATCH', body }),
      transformResponse: (r) => dataOne<AgentFactory>(r),
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'AgentFactory', id }, { type: 'AgentFactory', id: 'LIST' }]
          : [{ type: 'AgentFactory', id: 'LIST' }],
    }),

    deleteAgentFactory: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/agent/factories/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'AgentFactory', id: 'LIST' }],
    }),

    /** POST /agent/factories/:id/clone — deep copy (AF-7). */
    cloneAgentFactory: builder.mutation<
      AgentFactory,
      { id: string; name?: string; overrides?: Partial<CreateFactoryDto> }
    >({
      query: ({ id, ...body }) => ({
        url: `/agent/factories/${id}/clone`,
        method: 'POST',
        body: body && Object.keys(body).length > 0 ? body : {},
      }),
      transformResponse: (r) => dataOne<AgentFactory>(r),
      invalidatesTags: [{ type: 'AgentFactory', id: 'LIST' }],
    }),

    // ── Triggers (AF-4) ───────────────────────────────────────────────────
    getAgentTriggers: builder.query<TriggerBinding[], TriggerListParams | void>({
      query: () => '/agent/triggers',
      transformResponse: (response: { data: TriggerBinding[] }, _meta, arg) => {
        const triggers = dataList<TriggerBinding>(response);
        const factoryId = (arg as TriggerListParams | undefined)?.factoryId;
        return factoryId ? triggers.filter((t) => t.factoryId === factoryId) : triggers;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'AgentTrigger' as const, id: _id })),
              { type: 'AgentTrigger', id: 'LIST' },
            ]
          : [{ type: 'AgentTrigger', id: 'LIST' }],
    }),

    getAgentTrigger: builder.query<TriggerBinding, string>({
      query: (id) => `/agent/triggers/${id}`,
      transformResponse: (r) => dataOne<TriggerBinding>(r),
      providesTags: (_result, _error, id) => [{ type: 'AgentTrigger', id }],
    }),

    createAgentTrigger: builder.mutation<TriggerBinding, CreateTriggerDto>({
      query: (body) => ({ url: '/agent/triggers', method: 'POST', body }),
      transformResponse: (r) => dataOne<TriggerBinding>(r),
      invalidatesTags: [{ type: 'AgentTrigger', id: 'LIST' }],
    }),

    updateAgentTrigger: builder.mutation<TriggerBinding, { id: string; body: UpdateTriggerDto }>({
      query: ({ id, body }) => ({ url: `/agent/triggers/${id}`, method: 'PATCH', body }),
      transformResponse: (r) => dataOne<TriggerBinding>(r),
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'AgentTrigger', id }, { type: 'AgentTrigger', id: 'LIST' }]
          : [{ type: 'AgentTrigger', id: 'LIST' }],
    }),

    deleteAgentTrigger: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/agent/triggers/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'AgentTrigger', id: 'LIST' }],
    }),
  }),
});

// Export auto-generated hooks (endpoints injected onto the shared apiSlice).
export const {
  useGetAgentProvidersQuery,
  useGetAgentProviderQuery,
  useCreateAgentProviderMutation,
  useUpdateAgentProviderMutation,
  useDeleteAgentProviderMutation,
  useTestAgentProviderMutation,
  useGetAgentModelsQuery,
  useGetAgentModelQuery,
  useCreateAgentModelMutation,
  useUpdateAgentModelMutation,
  useDeleteAgentModelMutation,
  useTestAgentModelMutation,
  useGetAgentModelCollectionsQuery,
  useGetAgentModelCollectionQuery,
  useCreateAgentModelCollectionMutation,
  useUpdateAgentModelCollectionMutation,
  useDeleteAgentModelCollectionMutation,
  useGetAgentFactoriesQuery,
  useGetAgentFactoryQuery,
  useCreateAgentFactoryMutation,
  useUpdateAgentFactoryMutation,
  useDeleteAgentFactoryMutation,
  useCloneAgentFactoryMutation,
  useGetAgentTriggersQuery,
  useGetAgentTriggerQuery,
  useCreateAgentTriggerMutation,
  useUpdateAgentTriggerMutation,
  useDeleteAgentTriggerMutation,
} = agentFactoryApi;